import React, { useMemo, useState } from 'react';
import { ArrowRight, Check, ChevronDown, Info, Leaf, Search } from 'lucide-react';
import { RegulationLink } from '../ui/RegulationLink';
const AO_URL = 'https://www.fda.gov.ph/wp-content/uploads/2021/03/Administrative-Order-No.-2014-0030.pdf';
const CFRR_URL = 'https://www.fda.gov.ph/draft-for-comments-revised-rules-and-regulations-on-the-licensing-of-food-establishments-and-registration-of-processed-food-and-other-food-products-and-for-other-purposes-repealing-administrative/';
const AO_2016_URL = 'https://233773342789-lic.s3.eu-central-1.amazonaws.com/attachments/legislation/philippines/EMB%20Administrative%20Order%20No.0003-2016%20-%20Food%20and%20Drug_Part%201_15.02.2016.pdf';
const BC_URL = 'https://www.fda.gov.ph/wp-content/uploads/2021/05/Bureau-Circular-No.-2006-016.pdf';
const RA_URL = 'https://elibrary.judiciary.gov.ph/thebookshelf/showdocs/2/5607';
type Ingredient = {
  id: string;
  name: string;
  amount?: string;
  note: string;
  source: {
    text: string;
    url: string;
  };
  extraSources?: {
    text: string;
    url: string;
  }[];
};
const CXS_283 = {
  text: 'CXS 283-1978, §3.1 and §3.2',
  url: 'https://www.fao.org/fao-who-codexalimentarius/sh-proxy/en/?lnk=1&url=https%3A%2F%2Fworkspace.fao.org%2Fsites%2Fcodex%2FStandards%2FCXS+283-1978%2FCXS_283e.pdf'
};
const GSFA_CRACKERS = {
  text: 'GSFA 07.1.2',
  url: 'https://www.fao.org/gsfaonline/foods/details.html?id=118'
};
const CODEX_GFSA = {
  text: 'CODEX GFSA',
  url: 'https://www.fao.org/fao-who-codexalimentarius/sh-proxy/de/?lnk=1&url=https%253A%252F%252Fworkspace.fao.org%252Fsites%252Fcodex%252FStandards%252FCXS%2B192-1995%252FCXS_192e.pdf'
};
type Group = {
  title: string;
  caption: string;
  ratio: string;
  ingredients: Ingredient[];
};
const groups: Group[] = [{
  title: 'Cheese',
  caption: 'Compound ingredient · 79% of formulation',
  ratio: '4 / 4',
  ingredients: [{
    id: 'milk',
    name: 'Pasteurised Milk',
    note: 'Standard dairy ingredient - Permitted at GMP',
    source: {
      text: 'CXS 283-1978, §3.1 and §3.2',
      url: 'https://www.fao.org/fao-who-codexalimentarius/sh-proxy/en/?lnk=1&url=https%3A%2F%2Fworkspace.fao.org%2Fsites%2Fcodex%2FStandards%2FCXS+283-1978%2FCXS_283e.pdf'
    }
  }, {
    id: 'cultures',
    name: 'Cultures',
    note: 'Standard dairy ingredient - Permitted at GMP',
    source: {
      text: 'CXS 283-1978, §3.1 and §3.2',
      url: 'https://www.fao.org/fao-who-codexalimentarius/sh-proxy/en/?lnk=1&url=https%3A%2F%2Fworkspace.fao.org%2Fsites%2Fcodex%2FStandards%2FCXS+283-1978%2FCXS_283e.pdf'
    }
  }, {
    id: 'salt',
    name: 'Salt',
    note: 'Standard food ingredient, not an additive - Permitted at GMP',
    source: CXS_283
  }, {
    id: 'enzyme',
    name: 'Enzyme (Non-Animal Rennet)',
    note: 'Standard food ingredient - Permitted at GMP.',
    source: {
      text: 'AO 2026-0011',
      url: 'https://www.fda.gov.ph/wp-content/uploads/2026/07/Administrative-Order-2026-0011-1.pdf'
    },
    extraSources: [CXS_283, CODEX_GFSA]
  }]
}, {
  title: 'Brown Rice Crackers',
  caption: 'Compound ingredient · 21% of formulation',
  ratio: '5 / 5',
  ingredients: [{
    id: 'brown-rice',
    name: 'Brown Rice',
    amount: '48%',
    note: 'Standard food ingredient - Permitted at GMP.',
    source: GSFA_CRACKERS
  }, {
    id: 'potato-starch',
    name: 'Potato Starch',
    note: 'Standard food ingredient - Permitted at GMP.',
    source: GSFA_CRACKERS
  }, {
    id: 'sunflower-oil',
    name: 'Sunflower Oil',
    note: 'Standard food ingredient - Permitted at GMP.',
    source: {
      text: 'FDA 2021-027',
      url: 'https://www.fda.gov.ph/wp-content/uploads/2022/10/FDA-Circular-No.2021-028-A-20220329154106.pdf'
    }
  }, {
    id: 'flax',
    name: 'Flax Seeds',
    note: 'No PH-specific ban or warning requirement identified.',
    source: GSFA_CRACKERS
  }, {
    id: 'sesame',
    name: 'Black Sesame Seeds',
    note: "Also the ingredient source for the declared 'Sesame' allergen.",
    source: {
      text: 'Administrative Order No. 2016-0003',
      url: AO_2016_URL
    }
  }]
}];
const filters = [{
  id: 'all',
  label: 'All'
}, {
  id: 'permitted',
  label: 'Permitted',
  count: 9
}, {
  id: 'conditions',
  label: 'Permitted with conditions'
}, {
  id: 'verification',
  label: 'Verification required'
}, {
  id: 'exceeds',
  label: 'Exceeds limit'
}, {
  id: 'not-permitted',
  label: 'Not permitted'
}];
export function PhFormulaTab({
  onViewLabel
}: {
  onViewLabel: () => void;
}) {
  const [activeFilter, setActiveFilter] = useState('all');
  const [search, setSearch] = useState('');
  const visibleGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    const permittedOnly = activeFilter === 'all' || activeFilter === 'permitted';
    if (!permittedOnly) return [];
    return groups.map(group => ({
      ...group,
      ingredients: group.ingredients.filter(item => item.name.toLowerCase().includes(term))
    })).filter(group => group.ingredients.length > 0);
  }, [activeFilter, search]);
  return <div className="space-y-5" data-id="element-1842" data-mp="3eleh1oqt4b7z">
      <section className="rounded-2xl border border-[#dce3da] bg-white p-6 shadow-sm" aria-labelledby="ph-formula-heading" data-id="element-1843" data-mp="3emg4ksp0eq6l">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between" data-id="element-1844" data-mp="2lvo25dhcp5at">
          <div className="flex items-start gap-3" data-id="element-1845" data-mp="2zvfkfqewh7um">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eff3ed] text-forest-green" data-id="element-1846" data-mp="2zw8hfzo24izk">
              <Leaf className="h-5 w-5" data-id="element-1847" data-mp="2uygsneiplczl" />
            </span>
            <div data-id="element-1848" data-mp="f46qvr86vj29">
              <h2 id="ph-formula-heading" className="font-[Figtree] text-xl font-semibold text-evergreen" data-id="element-1849" data-mp="f3n682r5mgkj">
                Ingredients analysis
              </h2>
              <p className="mt-1 text-sm text-[#718174]" data-id="element-1850" data-mp="33ympqa483ub0">Deterministic per-ingredient verdict (Philippines / PH FDA).</p>
            </div>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full border border-forest-green/30 bg-forest-green/10 px-3 py-1.5 text-sm font-semibold text-forest-green" data-id="element-1851" data-mp="38zaqhni97pif">
            Low risk
          </span>
        </div>

        <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 font-mono text-xs font-semibold" data-id="element-1852" data-mp="2l858p2sr6n5h">
          <span className="text-[#8b968d]" data-id="element-1853" data-mp="2p8p8yf8mi6vu">0 Blocker</span>
          <span className="text-[#8b968d]" data-id="element-1854" data-mp="2p74e0oieclxg">0 Should fix</span>
          <span className="text-[#56685c]" data-id="element-1855" data-mp="2u78s67vu14ki">1 Consider</span>
        </div>

        <p className="mt-4 max-w-4xl text-sm leading-relaxed text-[#56685c]" data-id="element-1856" data-mp="2yzjv7jlvd6ha">
          Confirm the intended technological use of the enzyme in this product before you decide the declaration format. If the enzyme acts during
          processing only, and has no technological function in the finished product, it is a processing aid. Processing aids do not require an INS
          number.
        </p>
      </section>

      <section aria-label="Consider" className="space-y-3" data-id="element-1857" data-mp="1u152zv1geuob">
        <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-[#718174]" data-id="element-1858" data-mp="1p10izv1c7htc">Consider · 1</h2>
        <article className="rounded-2xl bg-[#f7faf5] p-5" data-id="element-1859" data-mp="1kzyk5fbc17rw">
          <div className="flex items-start gap-3" data-id="element-1860" data-mp="1l1i014t2tgt5">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#8b968d]" data-id="element-1861" data-mp="1g1dug9tpi6hp" />
            <div className="min-w-0" data-id="element-1862" data-mp="1b9vtvmvli7x9">
              <h3 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-1863" data-mp="1b8u5kgpkjoxl">Confirm whether the processing enzyme needs an INS number</h3>
              <span className="mt-2 inline-flex rounded-full bg-white px-2.5 py-1 text-xs font-medium text-[#617366]" data-id="element-1864" data-mp="1686j88cabw9p">Enzyme (Non-Animal Rennet)</span>
              <p className="mt-3 text-sm leading-relaxed text-[#56685c]" data-id="element-1865" data-mp="x8jd598m22ab">
                Confirm the intended technological use of the enzyme in this product. If the enzyme acts during processing only, and has no
                technological function in the finished product, it is a processing aid. Processing aids do not require an INS number. If the enzyme has
                a technological function in the finished product, it is a food additive. Additives require declaration by class name and specific name
                or INS number under AO 2014-0030.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3" data-id="element-1866" data-mp="1xgoej0aolj9c">
                <RegulationLink text="AO 2026-0011" href="https://www.fda.gov.ph/wp-content/uploads/2026/07/Administrative-Order-2026-0011-1.pdf" data-id="element-1867" data-mp="227eluj2o3w6o" />
                <RegulationLink text="Codex GFSA" href="https://www.fao.org/fao-who-codexalimentarius/sh-proxy/de/?lnk=1&url=https%253A%252F%252Fworkspace.fao.org%252Fsites%252Fcodex%252FStandards%252FCXS%2B192-1995%252FCXS_192e.pdf" data-id="element-1868" data-mp="1nk3e6s9lwwbu" />
                <RegulationLink text="AO 2014-0030" href={AO_URL} data-id="element-1869" data-mp="1jg5eoggddji9" />
                <button type="button" onClick={onViewLabel} className="inline-flex items-center gap-1 text-xs font-semibold text-forest-green underline-offset-2 hover:underline" data-id="element-1870" data-mp="zv527v0j0fpf">
                  See also: Packaging &amp; Label → Composition
                  <ArrowRight className="h-3.5 w-3.5" data-id="element-1871" data-mp="14u89lm3zay0k" />
                </button>
              </div>
            </div>
          </div>
        </article>
      </section>

      <section className="overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" aria-labelledby="ph-verdicts-heading" data-id="element-1872" data-mp="1irwmo7rt10v2">
        <div className="border-b border-[#e5ebe3] px-6 py-5" data-id="element-1873" data-mp="1skc2ozjjjfdb">
          <h2 id="ph-verdicts-heading" className="inline-flex items-center gap-2 font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-1874" data-mp="1nkzg8fwl0ab9">
            <Info className="h-4 w-4 text-[#738478]" data-id="element-1875" data-mp="21jz1ddkvy72o" />
            Ingredient verdicts
          </h2>
          <p className="mt-3 font-mono text-xs font-semibold uppercase tracking-[0.11em] text-[#6f8174]" data-id="element-1876" data-mp="1wmp6hieq937g">
            9 permitted · 0 permitted with conditions · 0 verification required · 0 exceeds limit · 0 not permitted
          </p>
        </div>

        <div className="flex flex-col gap-3 border-b border-[#e5ebe3] px-5 py-4 lg:flex-row lg:items-center lg:justify-between" data-id="element-1877" data-mp="3l7k39pw5gy4">
          <div className="flex flex-wrap gap-2" data-id="element-1878" data-mp="3pusbp1dlqkd0">
            {filters.map(filter => <button key={filter.id} type="button" onClick={() => setActiveFilter(filter.id)} aria-pressed={activeFilter === filter.id} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${activeFilter === filter.id ? 'border-forest-green bg-forest-green text-white' : 'border-[#dce3da] bg-white text-[#5d6f62] hover:border-[#b9c7ba]'}`} data-id="element-1879" data-mp="rccxzjc9tz5s">
                {filter.label}
                {filter.count !== undefined && <span className="font-mono" data-id="element-1880" data-mp="1colqrpnokc7o">{filter.count}</span>}
              </button>)}
          </div>
          <label className="relative w-full lg:w-64" data-id="element-1881" data-mp="1hn6mpcvwg9qb">
            <span className="sr-only" data-id="element-1882" data-mp="1ml8844gssl4y">Search ingredients</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9aa79c]" data-id="element-1883" data-mp="1mjnd6dqkn06k" />
            <input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search ingredients…" className="w-full rounded-lg border border-[#dce3da] bg-white py-2 pl-9 pr-3 text-sm text-evergreen placeholder:text-[#9aa79c] focus:border-forest-green focus:outline-none" data-id="element-1884" data-mp="1wbjmkky3ycq3" />
          </label>
        </div>

        <div className="space-y-5 p-5" data-id="element-1885" data-mp="ayqelx2qvoa2">
          {visibleGroups.length === 0 && <p className="px-1 text-sm italic text-[#9aa79c]" data-id="element-1886" data-mp="fx9ylrxs2pzs">No ingredients in this view.</p>}
          {visibleGroups.map(group => <div key={group.title} className="overflow-hidden rounded-xl border border-[#e1e8df]" data-id="element-1887" data-mp="7vu10hnn5sez">
              <div className="flex flex-wrap items-center justify-between gap-2 bg-[#f4f7f2] px-4 py-2.5" data-id="element-1888" data-mp="3w1xwkh0oeh0">
                <div data-id="element-1889" data-mp="hvt9kek29iru">
                  <p className="font-[Figtree] text-sm font-semibold text-evergreen" data-id="element-1890" data-mp="hw2xzxpgjhvf">{group.title}</p>
                  <p className="mt-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-[#8b968d]" data-id="element-1891" data-mp="cudlseqi0y5r">{group.caption}</p>
                </div>
                <span className="font-mono text-xs font-semibold text-[#728477]" data-id="element-1892" data-mp="munyzzcja5kx">{group.ratio}</span>
              </div>
              <div className="divide-y divide-[#edf0eb]" data-id="element-1893" data-mp="10jy7v6hy8bj9">
                {group.ingredients.map(ingredient => <IngredientRow key={ingredient.id} ingredient={ingredient} data-id="element-1894" data-mp="vmy8tfjio91n" />)}
              </div>
            </div>)}
        </div>
      </section>
    </div>;
}
function IngredientRow({
  ingredient
}: {
  ingredient: Ingredient;
}) {
  const [open, setOpen] = useState(false);
  return <div className="px-4 py-3.5" data-id="element-1895" data-mp="1p41mn4me17gd">
      <div className="flex items-start justify-between gap-4" data-id="element-1896" data-mp="1t898mu1go9aw">
        <div className="min-w-0" data-id="element-1897" data-mp="1t5eq5820cyk4">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1" data-id="element-1898" data-mp="1f8hpyg0024qk">
            <p className="font-[Figtree] text-sm font-semibold text-evergreen" data-id="element-1899" data-mp="1k72qkgj5yqfj">{ingredient.name}</p>
            {ingredient.amount && <span className="text-sm text-[#718174]" data-id="element-1900" data-mp="15hn3baiud34l">— {ingredient.amount}</span>}
          </div>
          <p className="mt-2 text-xs leading-relaxed text-[#748477]" data-id="element-1901" data-mp="1a9eunn9tncvs">{ingredient.note}</p>
          <p className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-[#8b968d]" data-id="element-1902" data-mp="whqjbm7ecsfv">
            Source: <RegulationLink text={ingredient.source.text} href={ingredient.source.url} className="align-middle" data-id="element-1903" data-mp="11gbkpoydxi5w" />
            {ingredient.extraSources?.map(extra => <RegulationLink key={extra.text} text={extra.text} href={extra.url} className="align-middle" data-id="element-1904" data-mp="mldqeqjoeim6" />)}
          </p>
          {open && <div className="mt-2 rounded-lg bg-[#f4f7f2] p-3 text-xs leading-relaxed text-[#607266]" data-id="element-1905" data-mp="1us9o47r9bsfp">
              Verdicts are assessed against the Philippine FDA labelling and food-additive framework using the declared ingredient name, function and
              proportion of the formulation.
            </div>}
        </div>
        <div className="flex shrink-0 items-center gap-2" data-id="element-1906" data-mp="1g2apy4zpch08">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-forest-green/10 px-2.5 py-1 text-xs font-semibold text-forest-green" data-id="element-1907" data-mp="1b6d8twgm76me">
            <Check className="h-3 w-3" data-id="element-1908" data-mp="17636cts16yca" />
            Permitted
          </span>
          <button type="button" onClick={() => setOpen(state => !state)} aria-expanded={open} aria-label={`Toggle detail for ${ingredient.name}`} className="rounded-lg p-1.5 text-[#718174] hover:bg-[#f2f5f1]" data-id="element-1909" data-mp="124vyyvtsw165">
            <ChevronDown className={`h-4 w-4 transition ${open ? 'rotate-180' : ''}`} data-id="element-1910" data-mp="2rkva2igzc0of" />
          </button>
        </div>
      </div>
    </div>;
}